import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/context';
import { useProAuth } from '@/lib/ProAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Loader2, Home, Crown, User, LogOut, Zap, 
  CheckCircle, CreditCard, Settings, Send, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import SmsWalletCard from '@/components/SmsWalletCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PRO Dashboard Page
 * Shows user profile, subscription status, and PRO features
 */
const ProDashboardPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, isAuthenticated, isPro, loading: authLoading, logout, refreshUser } = useProAuth();
  
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/pro/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleUpgradeToPro = async (planId = 'individual_pro') => {
    setSubscriptionLoading(true);
    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/pro-subscription/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          origin_url: originUrl,
          plan_id: planId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      toast.error(error.message);
      setSubscriptionLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const subscription = user?.subscription;

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-orange-500" data-testid="logo">
            KEIKKA
          </Link>
          <div className="flex items-center gap-4">
            {isPro && (
              <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                <Crown className="h-3 w-3 mr-1" />
                PRO
              </Badge>
            )}
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome{user?.name ? `, ${user.name}` : ''}!
          </h1>
          <p className="text-gray-600">
            Manage your PRO subscription and access exclusive features.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Profile Card */}
          <Card data-testid="profile-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Your Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name || 'Profile'} 
                    className="w-16 h-16 rounded-full"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                    <User className="h-8 w-8 text-orange-500" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-lg">{user?.name || 'KEIKKA User'}</p>
                  <p className="text-gray-500">{user?.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Card */}
          <Card data-testid="subscription-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isPro ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </Badge>
                    <Badge className="bg-yellow-100 text-yellow-700">
                      <Crown className="h-3 w-3 mr-1" />
                      PRO Monthly
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    Your PRO subscription is active. You have access to all PRO features.
                  </p>
                  {subscription?.current_period_end && (
                    <p className="text-xs text-gray-500">
                      Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-600">
                    Upgrade to PRO to unlock Quick Response and get faster replies from providers.
                  </p>
                  <div className="text-2xl font-bold text-orange-600">
                    €9.99<span className="text-sm font-normal text-gray-500">/month</span>
                  </div>
                  <Button 
                    className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
                    onClick={handleUpgradeToPro}
                    disabled={subscriptionLoading}
                    data-testid="upgrade-btn"
                  >
                    {subscriptionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Crown className="h-4 w-4 mr-2" />
                    )}
                    Upgrade to PRO
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* SMS Wallet Section */}
        <div className="mt-6">
          <SmsWalletCard />
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/">
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <Send className="h-5 w-5 text-orange-500" />
                    </div>
                    <div>
                      <p className="font-medium">New Request</p>
                      <p className="text-sm text-gray-500">Submit a service request</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/browse-requests">
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Home className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">Browse Requests</p>
                      <p className="text-sm text-gray-500">View open requests</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            {isPro && (
              <Card className="border-2 border-yellow-200 bg-yellow-50/50 h-full">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Zap className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-medium">Quick Response</p>
                      <p className="text-sm text-gray-500">PRO feature unlocked!</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* PRO Features Info */}
        {isPro && (
          <Card className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <Crown className="h-5 w-5 text-yellow-600" />
                Your PRO Benefits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Zap, text: "Quick Response - instant SMS notifications" },
                  { icon: CheckCircle, text: "Priority placement in queues" },
                  { icon: Send, text: "Unlimited service requests" },
                  { icon: CreditCard, text: "Cancel anytime" }
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-700">
                    <feature.icon className="h-4 w-4 text-orange-500" />
                    <span className="text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ProDashboardPage;
